import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Calculator, Calendar, Printer, Download, ChevronRight, Scale } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { DateInput } from "../components/ui/date-input";

const HearingCalculator = () => {
  const { user } = useAuth();
  const courtBranch = user?.court_branch || "Πρωτοδικείο Αθηνών";
  const [filingDate, setFilingDate] = useState("");
  const [procedure, setProcedure] = useState("Νέα Τακτική Διαδικασία Ν.5221/2025");
  const [residence, setResidence] = useState("domestic");
  const [isState, setIsState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fmtDate = (d) => {
    if (!d) return "-";
    const parts = d.split("-");
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
  };

  const handleCalculate = async () => {
    if (!filingDate) { toast.error("Εισάγετε ημερομηνία κατάθεσης"); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API}/calculator/deadlines`, {
        filing_date: filingDate,
        court_type: "Μονομελές",
        procedure: procedure,
        defendant_residence: residence,
        is_state_party: isState,
        gak_year: filingDate.split("-")[0]
      });
      setResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Σφάλμα υπολογισμού");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!result) return;
    const rows = result.deadlines.filter(d => !d.category).map(d =>
      `<tr><td>${d.title}</td><td>${d.description}</td><td style="font-weight:600">${fmtDate(d.deadline_date)}</td></tr>`
    ).join("");
    
    const parallelRows = result.deadlines.filter(d => d.category === "parallel").map(d =>
      `<tr><td>${d.title}</td><td>${d.description}</td><td style="font-weight:600">${fmtDate(d.deadline_date)}</td></tr>`
    ).join("");
    
    const remedyRows = (result.legal_remedies || []).map(r =>
      `<tr><td>${r.title}</td><td>${r.deadline}</td><td>${r.description}</td></tr>`
    ).join("");
    
    const rangeHtml = result.hearing_range ? `
      <div style="margin:16px 0;padding:12px;background:#ecfdf5;border:2px solid #059669;border-radius:8px;">
        <h3 style="color:#059669;margin:0 0 8px 0;">\u03A0\u03B9\u03B8\u03B1\u03BD\u03CC \u0394\u03B9\u03AC\u03C3\u03C4\u03B7\u03BC\u03B1 \u0394\u03B9\u03BA\u03B1\u03C3\u03AF\u03BC\u03BF\u03C5</h3>
        <p style="font-size:16px;font-weight:700;color:#065f46;">${fmtDate(result.hearing_range.start)} \u03AD\u03C9\u03C2 ${fmtDate(result.hearing_range.end)}</p>
        <p style="font-size:12px;color:#047857;">\u039F\u03BB\u03BF\u03BA\u03BB\u03AE\u03C1\u03C9\u03C3\u03B7 \u03C0\u03C1\u03BF\u03B4\u03B9\u03BA\u03B1\u03C3\u03C4\u03B9\u03BA\u03BF\u03CD: ${fmtDate(result.hearing_range.prodikastikos_end)} | 20-60 \u03B7\u03BC\u03AD\u03C1\u03B5\u03C2 \u03BC\u03B5\u03C4\u03AC</p>
      </div>` : "";
    
    const daysHtml = result.total_days ? `
      <div style="margin:16px 0;padding:12px;background:${result.total_days.is_within_law ? '#f0fdf4' : '#fef2f2'};border:2px solid ${result.total_days.is_within_law ? '#16a34a' : '#dc2626'};border-radius:8px;">
        <h3 style="color:${result.total_days.is_within_law ? '#16a34a' : '#dc2626'};margin:0 0 8px 0;">\u03A3\u03CD\u03BD\u03BF\u03BB\u03BF \u0397\u03BC\u03B5\u03C1\u03CE\u03BD \u03A0\u03C1\u03BF\u03B4\u03B9\u03BA\u03B1\u03C3\u03AF\u03B1\u03C2</h3>
        <div style="display:flex;gap:20px;margin:8px 0;">
          <div><strong>${result.total_days.prodikasia_days}</strong> \u03B7\u03BC. \u03C0\u03C1\u03BF\u03B4\u03B9\u03BA\u03B1\u03C3\u03AF\u03B1</div>
          <div><strong>${result.total_days.hearing_min_days}</strong> \u03B7\u03BC. \u03AD\u03C9\u03C2 \u03B4\u03B9\u03BA\u03AC\u03C3\u03B9\u03BC\u03BF (\u03B5\u03BB\u03AC\u03C7.)</div>
          <div><strong>${result.total_days.hearing_max_days}</strong> \u03B7\u03BC. \u03AD\u03C9\u03C2 \u03B4\u03B9\u03BA\u03AC\u03C3\u03B9\u03BC\u03BF (\u03BC\u03AD\u03B3.)</div>
        </div>
        <p style="font-size:12px;font-weight:600;color:${result.total_days.is_within_law ? '#16a34a' : '#dc2626'};">${result.total_days.law_message}</p>
      </div>` : "";
    
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>\u03A5\u03C0\u03BF\u03BB\u03BF\u03B3\u03B9\u03C3\u03BC\u03CC\u03C2 \u0394\u03B9\u03BA\u03B1\u03C3\u03AF\u03BC\u03BF\u03C5</title>
    <style>body{font-family:'Segoe UI',Tahoma,sans-serif;margin:20px;color:#1e293b;font-size:12px}
    h1{color:#1e3a5f;font-size:18px;border-bottom:2px solid #1e3a5f;padding-bottom:8px}
    table{width:100%;border-collapse:collapse;margin:12px 0}th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left;font-size:11px}
    th{background:#f1f5f9;font-weight:600}.params{display:flex;gap:16px;margin:12px 0;flex-wrap:wrap}
    .params div{background:#f1f5f9;padding:6px 12px;border-radius:6px;font-size:12px}
    .footer{margin-top:20px;font-size:10px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:8px}
    @media print{body{margin:10mm}}</style></head><body>
    <h1>${courtBranch} - \u03A5\u03C0\u03BF\u03BB\u03BF\u03B3\u03B9\u03C3\u03BC\u03CC\u03C2 \u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03B9\u03CE\u03BD & \u0394\u03B9\u03BA\u03B1\u03C3\u03AF\u03BC\u03BF\u03C5</h1>
    <div class="params">
      <div>\u039A\u03B1\u03C4\u03AC\u03B8\u03B5\u03C3\u03B7: <strong>${filingDate}</strong></div>
      <div>\u0394\u03B9\u03B1\u03B4\u03B9\u03BA\u03B1\u03C3\u03AF\u03B1: <strong>${procedure}</strong></div>
      <div>\u039A\u03B1\u03C4\u03BF\u03B9\u03BA\u03AF\u03B1: <strong>${residence === "domestic" ? "\u0395\u03C3\u03C9\u03C4\u03B5\u03C1\u03B9\u03BA\u03BF\u03CD" : residence === "abroad" ? "\u0395\u03BE\u03C9\u03C4\u03B5\u03C1\u03B9\u03BA\u03BF\u03CD" : "\u0391\u03B3\u03BD\u03CE\u03C3\u03C4\u03BF\u03C5"}</strong></div>
      ${isState ? '<div>\u0394\u03B7\u03BC\u03CC\u03C3\u03B9\u03BF: <strong>\u039D\u03B1\u03B9</strong></div>' : ''}
    </div>
    ${rangeHtml}
    ${daysHtml}
    <table><thead><tr><th>\u03A3\u03C4\u03AC\u03B4\u03B9\u03BF</th><th>\u03A0\u03B5\u03C1\u03B9\u03B3\u03C1\u03B1\u03C6\u03AE</th><th>\u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B1</th></tr></thead>
    <tbody>${rows}</tbody></table>
    ${parallelRows ? `
    <h3 style="color:#92400e;margin:16px 0 8px;">\u03A0\u03B1\u03C1\u03AC\u03BB\u03BB\u03B7\u03BB\u03B5\u03C2 \u0394\u03B9\u03BA\u03BF\u03BD\u03BF\u03BC\u03B9\u03BA\u03AD\u03C2 \u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B5\u03C2 (\u039D.5282/2026)</h3>
    <table style="border-color:#fbbf24"><thead><tr><th>\u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B1</th><th>\u03A0\u03B5\u03C1\u03B9\u03B3\u03C1\u03B1\u03C6\u03AE</th><th>\u0388\u03C9\u03C2</th></tr></thead>
    <tbody>${parallelRows}</tbody></table>` : ""}
    ${remedyRows ? `
    <h3 style="color:#3730a3;margin:16px 0 8px;">\u0388\u03BD\u03B4\u03B9\u03BA\u03B1 \u039C\u03AD\u03C3\u03B1 (\u039D.5282/2026)</h3>
    <table style="border-color:#818cf8"><thead><tr><th>\u0388\u03BD\u03B4\u03B9\u03BA\u03BF \u039C\u03AD\u03C3\u03BF</th><th>\u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B1</th><th>\u03A0\u03B5\u03C1\u03B9\u03B3\u03C1\u03B1\u03C6\u03AE</th></tr></thead>
    <tbody>${remedyRows}</tbody></table>` : ""}
    <div class="footer">\u0395\u03BA\u03C4\u03C5\u03C0\u03CE\u03B8\u03B7\u03BA\u03B5: ${new Date().toLocaleDateString('el-GR')} | ${courtBranch}</div>
    </body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="calculator-title">
          <Calculator className="w-6 h-6 text-blue-600" />
          Υπολογισμός Δικασίμου
        </h1>
        <p className="text-slate-500 mt-1">Εισάγετε τα στοιχεία για υπολογισμό προθεσμιών και εύρος δικασίμου</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Παράμετροι</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Ημερομηνία Κατάθεσης</Label>
              <DateInput value={filingDate} onChange={e => setFilingDate(e.target.value)}
                className="mt-1" data-testid="calc-filing-date" />
            </div>
            <div>
              <Label>Διαδικασία</Label>
              <Select value={procedure} onValueChange={setProcedure}>
                <SelectTrigger className="mt-1" data-testid="calc-procedure"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Νέα Τακτική Διαδικασία Ν.5221/2025">Νέα Τακτική Διαδικασία Ν.5221/2025</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Κατοικία Εναγομένων</Label>
              <Select value={residence} onValueChange={setResidence}>
                <SelectTrigger className="mt-1" data-testid="calc-residence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="domestic">Εσωτερικού</SelectItem>
                  <SelectItem value="abroad">Εξωτερικού</SelectItem>
                  <SelectItem value="unknown">Αγνώστου Διαμονής</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Διάδικος το Ελληνικό Δημόσιο;</Label>
              <Select value={isState ? "yes" : "no"} onValueChange={v => setIsState(v === "yes")}>
                <SelectTrigger className="mt-1" data-testid="calc-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Όχι</SelectItem>
                  <SelectItem value="yes">Ναι</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleCalculate} disabled={loading || !filingDate} className="w-full"
            data-testid="calc-submit-btn">
            <Scale className="w-4 h-4 mr-2" />
            {loading ? "Υπολογισμός..." : "Υπολογισμός Προθεσμιών"}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <>
          {/* Σύνολο Ημερών Προδικασίας + Ν.5282 */}
          {result.total_days && (
            <Card className={`border-2 ${result.total_days.is_within_law ? "border-emerald-300 bg-emerald-50/30" : "border-red-300 bg-red-50/30"}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${result.total_days.is_within_law ? "bg-emerald-100" : "bg-red-100"}`}>
                    <Scale className={`w-7 h-7 ${result.total_days.is_within_law ? "text-emerald-600" : "text-red-600"}`} />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-slate-900">
                      Σύνολο Ημερών Προδικασίας
                    </h2>
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-xs text-slate-500">Προδικασία</p>
                        <p className="text-2xl font-bold text-slate-800" data-testid="prodikasia-days">{result.total_days.prodikasia_days} ημ.</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-xs text-slate-500">Έως Δικάσιμο (ελάχ.)</p>
                        <p className="text-2xl font-bold text-blue-700">{result.total_days.hearing_min_days} ημ.</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-xs text-slate-500">Έως Δικάσιμο (μέγ.)</p>
                        <p className="text-2xl font-bold text-blue-700">{result.total_days.hearing_max_days} ημ.</p>
                      </div>
                    </div>
                    <div className={`mt-3 p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                      result.total_days.is_within_law 
                        ? "bg-emerald-100 text-emerald-800" 
                        : "bg-red-100 text-red-800"
                    }`} data-testid="law-compliance-msg">
                      {result.total_days.is_within_law ? (
                        <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                      ) : (
                        <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                      )}
                      <span>{result.total_days.law_message}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {result.hearing_range && (
            <Card className="border-emerald-300 bg-emerald-50/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar className="w-6 h-6 text-emerald-600" />
                  <h2 className="text-lg font-bold text-emerald-800">Πιθανό Διάστημα Δικασίμου</h2>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-emerald-600 font-medium mb-1">Βάσει ολοκλήρωσης προδικαστικού (+20 έως +60 ημ.):</p>
                    <p className="text-xl font-bold text-emerald-700" data-testid="hearing-range">
                      {fmtDate(result.hearing_range.start)} — {fmtDate(result.hearing_range.end)}
                    </p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Ολοκλήρωση προδικαστικού: {fmtDate(result.hearing_range.prodikastikos_end)}
                    </p>
                  </div>
                  {result.hearing_range.art215_start && (
                    <div className="pt-3 border-t border-emerald-200">
                      <p className="text-xs text-blue-600 font-medium mb-1">{result.hearing_range.art215_note}:</p>
                      <p className="text-xl font-bold text-blue-700" data-testid="art215-range">
                        {fmtDate(result.hearing_range.art215_start)} — {fmtDate(result.hearing_range.art215_end)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Δικονομικά Στάδια ({result.deadlines.filter(d => !d.category).length})</CardTitle>
                  <CardDescription>Κύρια στάδια προδικασίας</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="calc-print-btn">
                  <Printer className="w-4 h-4 mr-1" /> Εκτύπωση
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {result.deadlines.filter(d => !d.category).map((d, i) => (
                  <div key={`dl-${d.type || d.title || i}`} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100"
                    data-testid={`calc-deadline-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900">{d.title}</p>
                      <p className="text-xs text-slate-500 truncate">{d.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-bold text-slate-800">{fmtDate(d.deadline_date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Παράλληλες Δικονομικές Προθεσμίες */}
          {result.deadlines.filter(d => d.category === "parallel").length > 0 && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg text-amber-800">
                  Παράλληλες Δικονομικές Προθεσμίες (Ν.5282/2026)
                </CardTitle>
                <CardDescription>Προσεπίκληση, Πρόσθετη Παρέμβαση, Ανακοίνωση Δίκης</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {result.deadlines.filter(d => d.category === "parallel").map((d) => (
                    <div key={`pl-${d.type || d.title}`} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <Scale className="w-4 h-4 text-amber-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-amber-900">{d.title}</p>
                        <p className="text-xs text-amber-700">{d.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-bold text-amber-800">{fmtDate(d.deadline_date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ένδικα Μέσα */}
          {result.legal_remedies && result.legal_remedies.length > 0 && (
            <Card className="border-indigo-200">
              <CardHeader>
                <CardTitle className="text-lg text-indigo-800">
                  Ένδικα Μέσα (Ν.5282/2026)
                </CardTitle>
                <CardDescription>Προθεσμίες μετά τη δημοσίευση απόφασης - Αύγουστος εξαιρείται</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {result.legal_remedies.map((r) => (
                    <div key={`rm-${r.type || r.title}`} className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Scale className="w-4 h-4 text-indigo-700" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm text-indigo-900">{r.title}</p>
                        <p className="text-xs text-indigo-700 font-semibold mt-0.5">{r.deadline}</p>
                        <p className="text-xs text-indigo-600 mt-0.5">{r.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default HearingCalculator;
